import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, message, Space, Tag, Popconfirm, Card, Grid, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import cdnService from '../../services/cdnService';

import { userPath } from '../../../shared/config/paths';
import { formatDateTime } from '../../../shared/utils/date';
import PageHeader from '../../../shared/components/PageHeader';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';

const { useBreakpoint } = Grid;

export default function Workers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  useEffect(() => {
    fetchWorkers();
  }, [pagination.current, pagination.pageSize]);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const res = await cdnService.getWorkers(
        null,
        pagination.current,
        pagination.pageSize
      );

      if (res.success) {
        setWorkers(res.data?.workers || []);
        setPagination(prev => ({
          ...prev,
          total: res.data?.total || 0,
        }));
      }
    } catch (error) {
      message.error(t('Failed to load: {{message}}', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate(userPath('/cdn/workers/new'));
  };

  const handleView = (worker) => {
    navigate(userPath(`/cdn/workers/${worker.id}`));
  };

  const handleDelete = async (id) => {
    try {
      const res = await cdnService.deleteWorker(id);
      if (res.success) {
        message.success(t('Deleted successfully'));
        fetchWorkers();
      } else {
        message.error(res.error || t('Deletion failed'));
      }
    } catch (error) {
      message.error(t('Deletion failed: {{message}}', { message: error.message }));
    }
  };

  const handleToggle = async (worker) => {
    try {
      const res = await cdnService.toggleWorker(worker.id, !worker.enabled);
      if (res.success) {
        message.success(worker.enabled ? t('Disabled') : t('Enabled'));
        fetchWorkers();
      } else {
        message.error(res.error || t('Operation failed'));
      }
    } catch (error) {
      message.error(t('Operation failed: {{message}}', { message: error.message }));
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      width: 200,
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: t('Version'),
      dataIndex: 'version',
      width: 80,
    },
    {
      title: t('Status'),
      dataIndex: 'enabled',
      width: 100,
      render: (enabled) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? t('Enabled') : t('Disabled')}
        </Tag>
      ),
    },
    {
      title: t('Updated at'),
      dataIndex: 'updated_at',
      width: 180,
      render: (v) => formatDateTime(v),
    },
    {
      title: t('Actions'),
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >{t('Details')}</Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggle(record)}
          >
            {record.enabled ? t('Disable') : t('Enable')}
          </Button>
          <Popconfirm
            title={t('Delete this Worker?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('OK')}
            cancelText={t('Cancel')}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >{t('Delete')}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Mobile card renderer
  const renderMobileCard = (worker) => (
    <Card
      key={worker.id}
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {worker.name}
          </span>
          <Tag color={worker.enabled ? 'green' : 'default'} style={{ marginLeft: 8, flexShrink: 0 }}>
            {worker.enabled ? t('Enable') : t('Disable')}
          </Tag>
        </div>
      }
      onClick={() => handleView(worker)}
      hoverable
    >
      {worker.description && (
        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
          {worker.description}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Tag color="blue">v{worker.version}</Tag>
        <span style={{ fontSize: 12, color: '#999' }}>{formatDateTime(worker.updated_at)}</span>
      </div>
      <Space wrap size="small" onClick={(e) => e.stopPropagation()}>
        <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(worker)}>{t('Details')}</Button>
        <Button size="small" onClick={() => handleToggle(worker)}>
          {worker.enabled ? t('Disable') : t('Enable')}
        </Button>
        <Popconfirm
          title={t('Delete this Worker?')}
          onConfirm={() => handleDelete(worker.id)}
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Button size="small" danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div className="workers-page" style={{ padding: isMobile ? '12px 0' : 0 }}>
      <PageHeader
        title={t('Edge functions')}
        subtitle={t('JavaScript Workers running at the CDN edge that can be bound to CDN domains')}
        extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? t('Create') : t('Create Worker')}
        </Button>
        }
      />

      {isMobile ? (
        <div>
          {workers.map(worker => renderMobileCard(worker))}
          {workers.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>{t('No Workers yet')}</div>
          )}
          {pagination.total > pagination.pageSize && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Space>
                <Button
                  size="small"
                  disabled={pagination.current <= 1}
                  onClick={() => setPagination({ ...pagination, current: pagination.current - 1 })}
                >{t('Previous page')}</Button>
                <span style={{ fontSize: 12, color: '#666' }}>
                  {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)}
                </span>
                <Button
                  size="small"
                  disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                  onClick={() => setPagination({ ...pagination, current: pagination.current + 1 })}
                >{t('Next page')}</Button>
              </Space>
            </div>
          )}
        </div>
      ) : (
        <Table
                    onRow={rowLink(navigate, (r) => `/cdn/workers/${r.id}`)}
                    rowClassName={() => ROW_LINK_CLASS}
          columns={columns}
          dataSource={workers}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('{{total}} total', { total }),
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
            },
          }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description={t('No Workers yet. Click "Create" in the top right.')} /> }}
        />
      )}
    </div>
  );
}
